import type { FeatureCollection } from "geojson";

const M_PER_DEG_LAT = 111320;

// Direction-arrow anchor points spaced every ~intervalM meters along a route,
// each carrying the travel `bearing` (degrees clockwise from north) so an
// up-pointing icon can be rotated to match. Anchoring to geography (rather than
// line-symbol placement) keeps the arrows in the same spots across zooms.
export const routeArrowPoints = (
  coords: [number, number][] | undefined,
  intervalM = 280,
): FeatureCollection => {
  const features: FeatureCollection["features"] = [];
  if (!coords || coords.length < 2) {
    return { type: "FeatureCollection", features };
  }

  let travelled = 0;
  let nextAt = intervalM / 2;
  for (let i = 0; i < coords.length - 1; i++) {
    const [lng1, lat1] = coords[i];
    const [lng2, lat2] = coords[i + 1];
    const mLon = M_PER_DEG_LAT * Math.cos((lat1 * Math.PI) / 180);
    const dx = (lng2 - lng1) * mLon;
    const dy = (lat2 - lat1) * M_PER_DEG_LAT;
    const segLen = Math.hypot(dx, dy);
    if (segLen === 0) continue;
    // atan2(east, north) gives the compass bearing (clockwise from north).
    const bearing = (Math.atan2(dx, dy) * 180) / Math.PI;

    while (nextAt <= travelled + segLen) {
      const t = (nextAt - travelled) / segLen;
      features.push({
        type: "Feature",
        geometry: {
          type: "Point",
          coordinates: [lng1 + (lng2 - lng1) * t, lat1 + (lat2 - lat1) * t],
        },
        properties: { bearing },
      });
      nextAt += intervalM;
    }
    travelled += segLen;
  }

  return { type: "FeatureCollection", features };
};
